import { EVENTS } from 'ia-util-pubsub';
import { lockPage } from 'ia-util-helpers';

// Export the DrawerComponent class for other modules to extend
export class IaDrawer extends HTMLElement {
  constructor() {
    super();
    this.id = this.dataset.drawerId;
    this.isOpen = this.classList.contains('is-open');
    this.overlay = this.querySelector('[data-drawer-overlay]');
    this.window = this.querySelector('[data-drawer-window]');
    // Stable handler refs, bound once. The two listeners below sit on nodes
    // OUTSIDE this element (document, and the triggers in the header), so they
    // outlive it unless connect/disconnect pair up; see connectedCallback.
    this.onOpenerClick = this.toggleDrawer.bind(this);
    this.onDocumentKeyup = (event) => {
      if (!this.isOpen) return;
      if (event.key === 'Escape') this.close();
    };

    // The overlay is a child, so it dies with the drawer: safe to bind once here.
    this.overlay?.addEventListener('click', this.close.bind(this));
  }

  connectedCallback() {
    // Unconditional, not only on the move below: a drawer that left its
    // #shopify-section wrapper is out of reach of the reload that replaces the
    // wrapper's contents, so without this sweep the old copy survives in <body>
    // and the new one stacks on top, one extra drawer per edit.
    // Same tag only: the close button inside the drawer carries the same
    // data-drawer-id, and an attribute-only match deleted it along with the
    // real twins.
    document.querySelectorAll(`${this.tagName.toLowerCase()}[data-drawer-id="${this.dataset.drawerId}"]`)
      .forEach((twin) => {
        if (twin !== this && !this.contains(twin)) twin.remove();
      });

    // The editor can only pick a block that sits inside its own section, so a
    // drawer parked in <body> takes every block it holds out of reach. It stays
    // where it was rendered there; the storefront still moves, which is what
    // keeps it clear of the header's stacking context.
    if (this.parentNode !== document.body && !window.Shopify?.designMode) {
      document.body.appendChild(this);
    }

    // Outward listeners live here, not in the constructor: a copy dropped by the
    // dedup above would otherwise keep answering the header trigger from the
    // grave (one click toggling several drawers) and keep itself alive through
    // the document listener. remove-then-add makes this idempotent, which it has
    // to be: the move above reruns this callback.
    this.drawerOpener = document.querySelectorAll(`[data-drawer-trigger="${this.dataset.drawerId}"]`);
    this.drawerOpener.forEach((opener) => {
      opener.removeEventListener('click', this.onOpenerClick);
      opener.addEventListener('click', this.onOpenerClick);
    });
    document.removeEventListener('keyup', this.onDocumentKeyup);
    document.addEventListener('keyup', this.onDocumentKeyup);
  }

  disconnectedCallback() {
    this.drawerOpener?.forEach((opener) => {
      opener.removeEventListener('click', this.onOpenerClick);
    });
    document.removeEventListener('keyup', this.onDocumentKeyup);
    // A drawer can be taken off the page while open: the search page swaps the
    // whole results area on a tab switch, and the filters drawer goes with it.
    // Its lock on the page must not outlive it, or the shopper is left with a
    // page that cannot scroll and nothing on screen to explain why.
    if (this.isOpen) {
      this.isOpen = false;
      lockPage(false);
    }
  }

  toggleDrawer(event) {
    event.preventDefault();
    this.isOpen = !this.isOpen;
    this.isOpen ? this.open() : this.close();
  }

  close() {
    this.classList.remove('is-open');
    this.isOpen = false;
    lockPage(false);
    window.iaTheme?.showExtraStickyElements?.();
  }
  
  open() {
    this.classList.add('is-open');
    this.isOpen = true;
    if (document.documentElement.scrollHeight > document.documentElement.clientHeight) {
      lockPage(true);
    }
    window.iaTheme?.hideExtraStickyElements?.();
    // Bubbles: the analytics bridges subscribe on document (ia-ga4-events,
    // ia-contentsquare-events).
    this.dispatchEvent(new CustomEvent(EVENTS.drawerOpened, {
      bubbles: true,
      detail: {
        drawerId: this.id
      }
    }));
  }
}


// Only define custom element if not already defined
if (!customElements.get('ia-drawer-component')) {
  customElements.define('ia-drawer-component', IaDrawer);
}


class IaDrawerClose extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.close.bind(this));
  }


  close() {
    // Own drawer first: a global lookup returns the FIRST match, which closed
    // someone else's copy whenever a stale twin was still in the document.
    // Search starts at the parent because this button carries the same
    // data-drawer-id, so closest() would return the button and recurse.
    const drawerId = this.dataset.drawerId;
    const isDrawer = (el) => el && typeof el.toggleDrawer === 'function';
    const drawer = this.parentElement?.closest(`[data-drawer-id="${drawerId}"]`)
      || [...document.querySelectorAll(`[data-drawer-id="${drawerId}"]`)].find(isDrawer);
    // capability, not attribute: another close button wears the same id
    if (isDrawer(drawer)) drawer.close();
  }
}

if (!customElements.get('ia-drawer-close')) {
  customElements.define('ia-drawer-close', IaDrawerClose);
}


// Export DrawerMenu class for completeness
export class IaDrawerMenu extends IaDrawer {
  constructor() {
    super();
    this.submenuOpener = this.querySelectorAll('[data-sub-menu-opener]');
    this.parentMenu = this.querySelector('[data-parent-menu]');
    this.isSubmenuOpen = false;
    this.submenuOpener?.forEach(submenuOpener => {
      submenuOpener.addEventListener('click', this.toggleSubmenu.bind(this));
    });
  }

  toggleSubmenu(event) {
    event.preventDefault();
    this.isSubmenuOpen = !this.isSubmenuOpen;
    const submenuId = event.currentTarget.dataset.subMenuOpener;
    const submenu = this.querySelector(`[data-sub-menu="${submenuId}"]`);
    submenu?.classList.toggle('is-open', this.isSubmenuOpen);
    if (submenu && submenu.classList.contains('is-open')) {
      this.parentMenu?.classList.add('ia-hidden');
    } else {
      this.parentMenu?.classList.remove('ia-hidden');
    }
    this.querySelectorAll(`[data-sub-menu]`)?.forEach(anotherSubmenu => {
      if (anotherSubmenu.dataset.subMenu !== submenuId) {
        anotherSubmenu.classList.remove('is-open');
      }
    });
  }

  close() {
    super.close();
    this.isSubmenuOpen = false;
    this.querySelectorAll(`[data-sub-menu]`)?.forEach(anotherSubmenu => {
      anotherSubmenu.classList.remove('is-open');
    });
    this.parentMenu?.classList.remove('ia-hidden');
  }
}

// Only define custom element if not already defined
if (!customElements.get('ia-menu-drawer')) {
  customElements.define('ia-menu-drawer', IaDrawerMenu);
}

class IaDropdown extends IaDrawer {
  constructor() {
    super();
  }
}

if (!customElements.get('ia-dropdown')) {
  customElements.define('ia-dropdown', IaDropdown);
}
